/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Divider,
  Input,
  Typography,
  message,
  Select,
  Modal,
} from 'antd'; // Added Select
import TextArea from 'antd/lib/input/TextArea';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import FileProgressContext from '~src/contexts/FileProgressContext';
import { queryCache, useMutation } from 'react-query';

import IconButton from '~components/_common/IconButton/IconButton';
import { PaperClipOutlined } from '@ant-design/icons';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';

import {
  deleteInstantMessageDraftAttachment,
  getInstantMessageAttachmentLocation,
  getInstantMessageAttachmentLocationVendor,
  getInstantMessageDraftAttachmentLocation,
} from '~src/services/api/movements.ts';
import {
  MESSAGE_TYPE_CONFIG,
  renderMessageTypeOption,
} from '~src/utils/messageTypeUtils';
import Viewer from '~components/atoms/Viewers/Viewer';

type InstantMessageAttachment = {
  file_guid: string;
  name: string;
};

type InstantMessage = {
  id: number;
  title: string;
  message: string;
  created_by_full_name?: string;
  created_at?: number;
  attachments?: InstantMessageAttachment[];
  type?: number;
  reference?: string;
  serial_number?: string;
};

export type InstantMessageLetterTabProps = {
  loading: boolean;
  error: boolean;
  isBusy: boolean;
  projectId: number;
  tenderId: number;
  messageData?: InstantMessage;
  onDirtyChange?: (isDirty: boolean) => void;
  resetNonce?: number;
  onSaveTitleMessage: (payload: {
    title: string;
    message: string;
    reference?: string;
  }) => void;
  onOpenFileByGuid?: (fileGuid: string, fileName?: string) => void;
  onRefetch?: () => void;
  readOnly?: boolean;
  type?: number;
  shareId?: string;
  shareKey?: string;
};

export const InstantMessageLetterTab = forwardRef<
  any,
  InstantMessageLetterTabProps
>(
  (
    {
      loading,
      error,
      isBusy,
      projectId,
      tenderId,
      messageData,
      onDirtyChange,
      resetNonce,
      onSaveTitleMessage,
      onOpenFileByGuid,
      onRefetch,
      readOnly = false,
      shareId,
      shareKey,
    },
    ref
  ) => {
    const intl = useIntl();
    const styles = useStyles(styleFn);

    const { setFileProgressContext, fileProgressContext } = useContext(
      FileProgressContext
    );

    // console.log('InstantMessageLetterTab render with messageData:', messageData);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const MAX_ATTACHMENT_SIZE_BYTES = 4 * 1024 * 1024; // 4 MB
    const MAX_ATTACHMENTS = 5;

    // == local state
    const [title, setTitle] = useState('');
    const [msgContent, setMsgContent] = useState('');
    const [messageReference, setMessageReference] = useState('GEN');

    useImperativeHandle(ref, () => ({
      save: () => {
        onSaveTitleMessage({
          title,
          message: msgContent,
          reference: messageReference,
        });
      },
    }));

    const messageReferenceOptions = MESSAGE_TYPE_CONFIG.filter(
      (config) => config.key !== 'ALL'
    ).map((config) => ({
      label: renderMessageTypeOption(config, intl),
      value: config.key,
    }));

    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [
      attachmentToDelete,
      setAttachmentToDelete,
    ] = useState<InstantMessageAttachment | null>(null);

    // Drawer state for file preview
    const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
    const [previewFile, setPreviewFile] = useState<{
      url: string;
      name: string;
      extension: string;
    } | null>(null);
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);

    const [
      doDeleteAttachment,
      { isLoading: isDeletingAttachment },
    ] = useMutation(deleteInstantMessageDraftAttachment, {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'attachment-delete-successful' }) ||
            'Attachment deleted'
        );
        onRefetch?.();
      },
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'attachment-delete-failed' }) ||
            'Failed to delete attachment'
        );
      },
    });

    useEffect(() => {
      if (!messageData) return;
      setTitle(messageData.title || '');
      setMsgContent(messageData.message || '');
      setMessageReference(messageData.reference || 'GEN');
    }, [messageData?.id]);

    useEffect(() => {
      if (!messageData) return;
      setTitle(messageData.title || '');
      setMsgContent(messageData.message || '');
      setMessageReference(messageData.reference || 'GEN');
    }, [resetNonce]);

    const hasChanges =
      (messageData?.title ?? '') !== title ||
      (messageData?.message ?? '') !== msgContent ||
      (messageData?.reference ?? 'GEN') !== messageReference;

    useEffect(() => {
      onDirtyChange?.(hasChanges);
    }, [hasChanges, onDirtyChange]);

    const openAttachmentPicker = () => {
      if (isBusy) return;
      fileInputRef.current?.click();
    };

    const onAttachmentSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      if (!picked.length) return;

      e.target.value = '';

      const existingCount = messageData?.attachments?.length ?? 0;
      const remainingSlots = Math.max(0, MAX_ATTACHMENTS - existingCount);

      if (remainingSlots <= 0) {
        message.error(`Maximum ${MAX_ATTACHMENTS} files.`);
        return;
      }

      const filesToUpload = picked.slice(0, remainingSlots);
      const oversize = filesToUpload.find(
        (f) => f.size > MAX_ATTACHMENT_SIZE_BYTES
      );
      if (oversize) {
        message.error(intl.formatMessage({ id: 'upload-max-4mb' }));
        return;
      }

      setFileProgressContext((prev) => [
        ...prev,
        ...filesToUpload.map(
          (file) =>
            ({
              id: `im_attach_${Date.now()}_${Math.random()
                .toString(16)
                .slice(2)}`,
              file,
              projectId,
              tenderId: String(tenderId),
              draftId: messageData?.id,
              versionStr: ' ',
              description: ' ',
              isDone: false,
              shouldStartUpload: true,
              isInstantMessageAttachment: true,
            } as any)
        ),
      ]);
    };

    // refetch
    useEffect(() => {
      if (!projectId || !tenderId || !messageData?.id) return;

      const hasPendingUploads = fileProgressContext?.some(
        (f: any) =>
          f?.isInstantMessageAttachment &&
          String(f?.draftId) === String(messageData.id) &&
          !f?.isDone
      );

      if (!hasPendingUploads && fileProgressContext?.length > 0) {
        onRefetch?.();
      }
    }, [fileProgressContext, projectId, tenderId, messageData?.id, onRefetch]);

    // Helper functions for drawer preview
    const isPreviewable = (filename: string): boolean => {
      const ext = filename.split('.').pop()?.toLowerCase() || '';
      const isPdf = /^pdf$/i.test(ext);
      const isImage = /^(jpg|jpeg|png|gif|webp)$/i.test(ext);
      return isPdf || isImage;
    };

    const handleDrawerClose = () => {
      setPreviewDrawerVisible(false);
      setPreviewFile(null);
    };

    const handleFileDownload = () => {
      if (previewFile?.url) {
        const link = document.createElement('a');
        link.href = previewFile.url;
        link.download = previewFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    const handleAttachmentPreview = async (
      fileGuid: string,
      filename: string
    ) => {
      if (!messageData?.id) return;

      const extension = filename.split('.').pop() || '';
      if (!isPreviewable(filename)) return;

      // Open drawer immediately with empty URL (shows loading spinner)
      setPreviewFile({ url: '', name: filename, extension });
      setPreviewDrawerVisible(true);
      setIsLoadingUrl(true);

      try {
        let url = '';

        if (shareId && shareKey) {
          // Vendor
          url = await getInstantMessageAttachmentLocationVendor({
            shareId,
            shareKey,
            instant_message_id: messageData.id,
            file_guid: fileGuid,
          });
        } else if (readOnly) {
          // Project ReadOnly
          url = await getInstantMessageAttachmentLocation({
            projectId,
            tenderId,
            instantMessageId: messageData.id,
            fileGuid: fileGuid,
          });
        } else {
          // Project Draft
          url = await getInstantMessageDraftAttachmentLocation({
            projectId,
            tenderId,
            draftId: messageData.id,
            fileGuid: fileGuid,
          });
        }

        if (!url) {
          message.error(
            intl.formatMessage({ id: 'attachment-download-failed' }) ||
              'Download failed'
          );
          setPreviewDrawerVisible(false);
          setPreviewFile(null);
          return;
        }

        // Update with actual URL - Viewer will re-render with content
        setPreviewFile({ url, name: filename, extension });
      } catch (e) {
        message.error(
          intl.formatMessage({ id: 'attachment-download-failed' }) ||
            'Download failed'
        );
        setPreviewDrawerVisible(false);
        setPreviewFile(null);
      } finally {
        setIsLoadingUrl(false);
      }
    };

    // == Download
    const handleDownloadAttachment = async (file_guid: string) => {
      if (!messageData?.id) return;
      let url = '';

      try {
        if (shareId && shareKey) {
          // Vendor
          url = await getInstantMessageAttachmentLocationVendor({
            shareId,
            shareKey,
            instant_message_id: messageData.id,
            file_guid,
          });
        } else if (readOnly) {
          // Project ReadOnly
          url = await getInstantMessageAttachmentLocation({
            projectId,
            tenderId,
            instantMessageId: messageData.id,
            fileGuid: file_guid,
          });
        } else {
          // Project Draft
          url = await getInstantMessageDraftAttachmentLocation({
            projectId,
            tenderId,
            draftId: messageData.id,
            fileGuid: file_guid,
          });
        }

        if (!url) {
          message.error(
            intl.formatMessage({ id: 'attachment-download-failed' }) ||
              'Download failed'
          );
          return;
        }

        window.open(url, '_blank');
      } catch (e) {
        message.error(
          intl.formatMessage({ id: 'attachment-download-failed' }) ||
            'Download failed'
        );
      }
    };

    const handleConfirmDelete = async () => {
      if (!attachmentToDelete || !messageData?.id) return;

      await doDeleteAttachment({
        projectId,
        tenderId,
        draftId: messageData.id,
        fileGuid: attachmentToDelete.file_guid,
      });

      setAttachmentToDelete(null);
      setShowConfirmDeleteModal(false);

      onRefetch?.();
    };

    const handleReset = () => {
      Modal.confirm({
        title: intl.formatMessage({ id: 'confirm-reset-title' }),
        content: intl.formatMessage({ id: 'confirm-reset-message' }),
        okText: intl.formatMessage({ id: 'reset' }),
        cancelText: intl.formatMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () => {
          setTitle(messageData?.title || '');
          setMsgContent(messageData?.message || '');
          setMessageReference(messageData?.reference || 'GEN');
        },
      });
    };

    if (loading) {
      return (
        <div css={styles.stateBox}>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'loading' })}
          </Typography.Text>
        </div>
      );
    }

    if (error || !messageData) {
      return (
        <div css={styles.stateBox}>
          <Typography.Text type="danger">
            Failed to load instant message draft
          </Typography.Text>
        </div>
      );
    }

    const currentConfig = MESSAGE_TYPE_CONFIG.find(
      (opt) => opt.key === messageReference
    );

    return (
      <div css={styles.wrapper}>
        {/* Title / Message Section */}
        <div css={styles.section}>
          <Typography.Title level={5} css={styles.sectionTitle}>
            {intl.formatMessage({ id: 'instant-message-details' }) +
              ' ' +
              `<${messageData?.serial_number}>`}
          </Typography.Title>

          <div css={styles.titleTypeGrid}>
            <div css={styles.formRowColumn}>
              <div css={styles.label}>
                {intl.formatMessage({ id: 'title' })}
              </div>
              {readOnly ? (
                <div
                  style={{
                    padding: '5px 0',
                    width: '100%',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}>
                  <Typography.Text
                    style={{
                      display: 'block',
                      width: '100%',
                    }}>
                    {title || '-'}
                  </Typography.Text>
                </div>
              ) : (
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isBusy}
                />
              )}
            </div>
            <div css={styles.formRowColumn}>
              <div css={styles.label}>Type</div>
              {readOnly ? (
                <div style={{ padding: '5px 0' }}>
                  {currentConfig
                    ? renderMessageTypeOption(currentConfig, intl)
                    : messageReference}
                </div>
              ) : (
                <Select
                  options={messageReferenceOptions}
                  value={messageReference}
                  onChange={(val) => setMessageReference(val)}
                  disabled={isBusy}
                  style={{ width: '100%' }}
                />
              )}
            </div>
          </div>

          <div css={styles.formRowColumn}>
            <div css={styles.label}>
              {intl.formatMessage({ id: 'message' })}
            </div>
            {readOnly ? (
              <div style={{ padding: '5px 0', whiteSpace: 'pre-wrap' }}>
                <Typography.Text>{msgContent || '-'}</Typography.Text>
              </div>
            ) : (
              <TextArea
                value={msgContent}
                onChange={(e) => setMsgContent(e.target.value)}
                rows={5}
                autoSize={{ minRows: 5, maxRows: 10 }}
                disabled={isBusy}
              />
            )}
          </div>

          {!readOnly && (
            <div css={styles.actionsRow}>
              <Button disabled={isBusy || !hasChanges} onClick={handleReset}>
                {intl.formatMessage({ id: 'reset' })}
              </Button>
              <div style={{ flex: 1 }} />
              <Button
                type="primary"
                disabled={isBusy || !hasChanges}
                onClick={() => {
                  onSaveTitleMessage({
                    title,
                    message: msgContent,
                    reference: messageReference,
                  });
                }}>
                {intl.formatMessage({ id: 'save' })}
              </Button>
            </div>
          )}
        </div>

        <Divider css={styles.divider} />

        {/* Attachments Section */}
        <div css={styles.section}>
          <Typography.Title level={5} css={styles.sectionTitle}>
            {intl.formatMessage({ id: 'correction-letter-files-title' })}
          </Typography.Title>

          <div css={styles.fileBox}>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}>
              <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text strong>
                  {intl.formatMessage({ id: 'correction-letter-attachments' })}
                </Typography.Text>
                {!readOnly && (
                  <Typography.Text
                    italic
                    type={'secondary'}
                    css={{ fontSize: '12px' }}>
                    ({intl.formatMessage({ id: 'upload-max-4mb' })}, max{' '}
                    {MAX_ATTACHMENTS})
                  </Typography.Text>
                )}
              </div>

              {!readOnly && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={onAttachmentSelected}
                  />

                  <Button
                    onClick={openAttachmentPicker}
                    disabled={
                      isBusy ||
                      (messageData.attachments?.length ?? 0) >= MAX_ATTACHMENTS
                    }>
                    {intl.formatMessage({ id: 'upload' })}
                  </Button>
                </div>
              )}
            </div>

            {messageData.attachments?.length ? (
              <div css={styles.attachmentsList}>
                {messageData.attachments.map((a) => {
                  const isPdf =
                    a.name?.toLowerCase()?.endsWith('.pdf') ||
                    a.name?.toLowerCase()?.includes('.pdf');

                  return (
                    <div
                      key={a.file_guid}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          minWidth: 0,
                          flex: 1,
                        }}>
                        {isPdf ? (
                          <IconButton
                            icon={'file-pdf'}
                            iconStyle={'fad'}
                            color={'red'}
                            backgroundOff
                            customStyle={{ fontSize: '18px' }}
                            containerStyle={{
                              maxHeight: '18px',
                              maxWidth: '18px',
                              minHeight: '18px',
                              minWidth: '18px',
                            }}
                          />
                        ) : (
                          <PaperClipOutlined
                            style={{ fontSize: '18px' }}
                            rev={''}
                          />
                        )}

                        <Button
                          type="link"
                          disabled={!onOpenFileByGuid}
                          onClick={() =>
                            onOpenFileByGuid?.(a.file_guid, a.name)
                          }
                          style={{
                            padding: 0,
                            height: 'auto',
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}>
                          {a.name}
                        </Button>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Preview icon for previewable files */}
                        {isPreviewable(a.name) && (
                          <IconButton
                            icon={'eye'}
                            iconStyle={'far'}
                            color={'red'}
                            customStyle={{ fontSize: '16px' }}
                            click={() =>
                              handleAttachmentPreview(a.file_guid, a.name)
                            }
                          />
                        )}

                        <IconButton
                          icon={'download'}
                          iconStyle={'far'}
                          color={'blue'}
                          click={() => handleDownloadAttachment(a.file_guid)}
                          customStyle={{ fontSize: '16px' }}
                        />

                        {!readOnly && (
                          <IconButton
                            icon={'trash'}
                            iconStyle={'far'}
                            color={'red'}
                            disabled={
                              isDeletingAttachment &&
                              attachmentToDelete?.file_guid === a.file_guid
                            }
                            click={() => {
                              setAttachmentToDelete(a);
                              setShowConfirmDeleteModal(true);
                            }}
                            customStyle={{ fontSize: '16px' }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Typography.Text type="secondary">
                {intl.formatMessage({
                  id: 'correction-letter-attachments-not-implemented',
                })}
              </Typography.Text>
            )}
          </div>
        </div>

        <SimpleConfirmModal
          visible={showConfirmDeleteModal}
          isLoading={isDeletingAttachment}
          onOk={handleConfirmDelete}
          onCancel={() => {
            setAttachmentToDelete(null);
            setShowConfirmDeleteModal(false);
          }}
          modalTitle={`${intl.formatMessage({ id: 'delete-attachment' })} - ${
            attachmentToDelete?.name
          }`}
          modalIcon={['far', 'trash']}
          okText={intl.formatMessage({ id: 'delete' })}
          okType="danger"
        />

        {/* Drawer preview */}
        {previewFile && (
          <Viewer
            mode="drawer"
            drawerVisible={previewDrawerVisible}
            onDrawerClose={handleDrawerClose}
            drawerTitle={previewFile.name}
            drawerWidth={800}
            fileUrl={previewFile.url}
            fileExtension={previewFile.extension}
            showDownloadButton={true}
            onDownload={handleFileDownload}
            isLoadingUrl={isLoadingUrl}
          />
        )}
      </div>
    );
  }
);

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingBottom: 18,
    paddingTop: 5,
  },
  stateBox: {
    padding: 16,
    background: '#fafafa',
    borderRadius: 6,
    border: '1px solid #f0f0f0',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sectionTitle: {
    margin: 0,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    alignItems: 'center',
    gap: 12,
  },
  titleTypeGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 200px', // Dropdown beside title on the right
    gap: 16,
    alignItems: 'end',
  },
  formRowColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  label: {
    fontWeight: 500,
  },
  actionsRow: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  divider: {
    margin: '4px 0',
  },
  fileBox: {
    border: '1px solid #f0f0f0',
    borderRadius: 6,
    padding: 12,
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minHeight: 80,
  },
  attachmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 6,
  },
});
